import { api, apiClient, config, computed, debug, form, formHelpers, Formsy, i18n, inject, mui, observable, observer, pr, React, RouteComponentProps, theme, toJS, ui } from "aaa";
import { Auth } from "models/Auth";
import { AMOUNT_RANGE_STEP, CONFIG_AMOUNT_MIN, CONFIG_AMOUNT_MAX, CONFIG_DURATION_MIN, CONFIG_DURATION_MAX, interestSetsMatch, InterestRates } from "models/InterestRates";
import { Loan, getDefaultAmount, getDefaultDuration } from "models/Loan";
import * as FileSaver from "file-saver";
import { numberToPercentageString, dateOnlyAsString } from "util/helpers";
import { RepresentativeExample } from "components/RepresentativeExample";
import * as _ from "lodash";
const intl = require("intl");
const DateTimeFormat = intl.DateTimeFormat;
require("intl/locale-data/jsonp/de");



type IProps = RouteComponentProps<{}> & i18n.InjectedIntlProps & {
    auth: Auth,
    interestRates: InterestRates,
    loan: Loan
};

const RepEx = (props: { interestRates: any, index: number, style?: React.CSSProperties }) => {

    return (
        <div style={{ display: "flex", flexDirection: "column", ...props.style }}>
            <pr.TextH4>Beispiel {props.index + 1}</pr.TextH4>
            <form.FormsyText
                required={true}
                name={"repExPayOut" + props.index}
                validations="isPositiveCurrency"
                validationError={ui.__("error.interestInvalid")}
                floatingLabelText={ui.__("representativeExample.payOut")}
                value={props.interestRates ? props.interestRates.repExes[props.index].payOut.toLocaleString(config.LOCALE) : undefined}
            />
            <form.FormsyText
                required={true}
                name={"repExInitialAmortization" + props.index}
                validations="isPositiveCurrency"
                validationError={ui.__("error.interestInvalid")}
                floatingLabelText={ui.__("representativeExample.initial")}
                value={props.interestRates ? props.interestRates.repExes[props.index].initialAmortization.toLocaleString(config.LOCALE) : undefined}
            />
            <form.FormsyText
                required={true}
                name={"repExInterest" + props.index}
                validations="isPositiveCurrency"
                validationError={ui.__("error.interestInvalid")}
                floatingLabelText={ui.__("representativeExample.interest")}
                value={props.interestRates ? props.interestRates.repExes[props.index].interest.toLocaleString(config.LOCALE) : undefined}
            />
            <div style={{ display: "flex" }}>
                <form.FormsyText
                    required={true}
                    name={"repExInterestEffective" + props.index}
                    validations="isPositiveCurrency"
                    validationError={ui.__("error.interestInvalid")}
                    floatingLabelText={ui.__("representativeExample.interestEffective")}
                    value={props.interestRates ? props.interestRates.repExes[props.index].interestEffective.toLocaleString(config.LOCALE) : undefined}
                />
                {props.index === 1 ? (
                    <div style={{ maxWidth: 256, paddingLeft: theme.MARGIN, display: "flex", alignItems: "center" }}>
                        <img
                            src={require("../assets/prozentsatz_logo_empty.png")}
                            alt="badge image"
                            style={{ pointerEvents: "none", height: 40, marginRight: 6 }}
                        />
                        {/* tslint:disable-next-line:max-line-length */}
                        <p style={{ fontSize: 14, fontStyle: "italic" }}>
                            <small>{ui.__("hint.effectiveRateLogoUsed")}</small>
                        </p>
                    </div>
                ) : (<div />)}
            </div>
            <form.FormsyText
                required={true}
                name={"repExMonthlyRate" + props.index}
                validations="isPositiveCurrency"
                validationError={ui.__("error.interestInvalid")}
                floatingLabelText={ui.__("representativeExample.monthlyRate")}
                value={props.interestRates ? props.interestRates.repExes[props.index].monthlyRate.toLocaleString(config.LOCALE) : undefined}
            />
            <form.FormsyText
                required={true}
                name={"repExNumRates" + props.index}
                validations="isInt"
                validationError={ui.__("error.onlyInt")}
                floatingLabelText={ui.__("representativeExample.numRates")}
                value={props.interestRates ? props.interestRates.repExes[props.index].numRates.toLocaleString(config.LOCALE) : undefined}
            />
            <form.FormsyText
                required={true}
                name={"repExDurationInYears" + props.index}
                validations="isInt"
                validationError={ui.__("error.onlyInt")}
                floatingLabelText={ui.__("loan.durationInYears")}
                value={props.interestRates ? props.interestRates.repExes[props.index].durationInYears.toLocaleString(config.LOCALE) : undefined}
            />
            <form.FormsyText
                required={true}
                name={"repExTotal" + props.index}
                validations="isPositiveCurrency"
                validationError={ui.__("error.interestInvalid")}
                floatingLabelText={ui.__("representativeExample.total.short")}
                value={props.interestRates ? props.interestRates.repExes[props.index].total.toLocaleString(config.LOCALE).replace(".", "") : undefined}
            />
        </div>
    );
};

function parseLocalFloat(value: string) {
    return parseFloat(value.replace(config.CURRENCY_DECIMAL_SEPARATOR, "."));
}


@i18n.injectIntl
@inject("auth", "interestRates", "loan")
@observer
export class InterestRatesForm extends React.Component<IProps, {}> {

    @observable
    formIsValid = false;

    @observable
    validFrom = new Date();

    @observable
    durationArray: number[] = []; // e.g. [1, 2] -> one year or two years -- will be populated after load

    @observable
    defaultDuration = 0;

    @observable
    defaultAmount = 0;

    private formRef: any = null;

    async componentWillMount() {
        if (!config.DEBUG && !this.props.auth.token) {
            this.props.history.push("/login");
            return;
        }

        window.scrollTo(0, 0);

        this.loadInterestRates();
    }

    loadInterestRates = async () => {
        try {
            await this.props.interestRates.load();
        } catch (error) {
            // ignore upcoming interest rates not found
            console.warn("err", error);
        }

        try {
            if (this.props.interestRates.interestRatesUpcoming) {
                this.validFrom = new Date(this.props.interestRates.interestRatesUpcoming.validFrom);
                this.durationArray = this.props.interestRates.durationArrayUpcoming;
                this.defaultDuration = getDefaultDuration(this.props.interestRates.interestRatesUpcoming.minDuration, this.props.interestRates.interestRatesUpcoming.maxDuration);
                this.defaultAmount = getDefaultAmount(this.props.interestRates.interestRatesUpcoming.minAmount, this.props.interestRates.interestRatesUpcoming.maxAmount);
            } else {
                this.validFrom = new Date();
                this.durationArray = this.props.interestRates.durationArray;
                this.defaultDuration = getDefaultDuration(this.props.interestRates.interestRates!.minDuration, this.props.interestRates.interestRates!.maxDuration);
                this.defaultAmount = getDefaultAmount(this.props.interestRates.interestRates!.minAmount, this.props.interestRates.interestRates!.maxAmount);
            }
        } catch (e) {
            console.error("unable to setup default state for form", e);
        }

    }

    handleError = (error: any) => {
        let message = "Server status code " + error.statusCode;
        if (error.statusCode === 401) {
            message = ui.__("error.loginForInterestRates");
            this.props.auth.token = undefined;
        }

        ui.showMessageBox({
            title: ui.__("error"),
            message,
            onOk: error.statusCode === 401 ? () => this.props.history.push("/login") : undefined
        });
    }

    onSubmit = async (fields: any, resetForm: any, invalidateForm: any) => {

        debug.log("### fields", fields);

        const interestSet = {
            validFrom: dateOnlyAsString(this.validFrom),
            interestInPercent: this.durationArray.map(durationInYears => {
                return parseLocalFloat(fields["interest" + durationInYears]);
            }),
            interestEffectiveInPercent: this.durationArray.map(durationInYears => {
                return parseLocalFloat(fields["interestEffective" + durationInYears]);
            }),
            repExes: [0, 1].map(index => ({
                amount: _.parseInt(fields.repExAmount),
                payOut: parseLocalFloat(fields["repExPayOut" + index]),
                initialAmortization: parseLocalFloat(fields["repExInitialAmortization" + index]),
                interest: parseLocalFloat(fields["repExInterest" + index]),
                interestEffective: parseLocalFloat(fields["repExInterestEffective" + index]),
                monthlyRate: parseLocalFloat(fields["repExMonthlyRate" + index]),
                numRates: _.parseInt(fields["repExNumRates" + index]),
                durationInYears: _.parseInt(fields["repExDurationInYears" + index]),
                total: parseLocalFloat(fields["repExTotal" + index])
            })),

            minAmount: _.parseInt(fields.minAmount),
            maxAmount: _.parseInt(fields.maxAmount),
            minDuration: _.parseInt(fields.minDuration),
            maxDuration: _.parseInt(fields.maxDuration),
        };

        // Early out, if no change
        if (this.props.interestRates.interestRatesUpcoming) {
            debug.log("### compare upcoming");
            if (interestSetsMatch(interestSet, this.props.interestRates.interestRatesUpcoming)) {
                return;
            }
        } else if (this.props.interestRates.interestRates) {
            debug.log("### compare current");
            if (interestSetsMatch(interestSet, this.props.interestRates.interestRates, false)) {
                return;
            }
        }

        try {
            const ratesRequest = api.postInterestRates(interestSet);
            const ratesResponse = await apiClient.request(ratesRequest, true);
            ui.showMessageBox({
                title: ui.__("ok"),
                message: ui.__("interestRatesSent"),
                onOk: () => {
                    this.props.auth.logout();
                    this.props.history.push("/login");

                    // reload the loan
                    this.props.loan.reload();
                }
            });
        } catch (error) {
            this.handleError(error);
        }
    }


    downloadHistory = async () => {
        try {
            const history = await apiClient.request(api.getInterestRatesHistory(), true);
            const blob = await history.blob();
            FileSaver.saveAs(blob, "zinsen-history.xlsx");
        } catch (err) {
            this.handleError(err);
        }
    }


    renderCurrent() {
        const current = this.props.interestRates.durationArray.map((value, index) => {
            return (
                <pr.HFlex
                    key={index}
                    style={{
                        width: "100%",
                        background: (value % 2) === 0 ? "white" : "#eee",
                        padding: 4
                    }}
                >
                    <div style={{ flex: 2 }}>
                        {value} {value === 1 ? "Jahr" : "Jahre"}
                    </div>
                    <div style={{ flex: 1 }}>
                        {this.props.interestRates.interestRates ?
                            this.props.interestRates.interestRates.interestInPercent[index].toLocaleString(config.LOCALE) :
                            "-"}
                    </div>
                    <div style={{ flex: 1 }}>
                        {this.props.interestRates.interestRates ?
                            this.props.interestRates.interestRates.interestEffectiveInPercent[index].toLocaleString(config.LOCALE) :
                            "-"}
                    </div>
                </pr.HFlex>
            );
        });

        current.unshift(<pr.HFlex
            key="header"
            style={{
                width: "100%",
                background: theme.SPARDA_BLUE,
                color: "white",
                padding: 4
            }}
        >
            <div style={{ flex: 2 }}>
                {ui.__("loan.duration")}
            </div>
            <div style={{ flex: 1 }}>
                {ui.__("loan.interestRate")}
            </div>
            <div style={{ flex: 1 }}>
                {ui.__("loan.interestRateEffective")}
            </div>
        </pr.HFlex>);

        const meta = (
            <div>
                <pr.HFlex
                    key="header"
                    style={{
                        width: "100%",
                        background: theme.SPARDA_BLUE,
                        color: "white",
                        padding: 4
                    }}
                >
                    <div style={{ flex: 2 }}>
                        {ui.__("setting")}
                    </div>
                    <div style={{ flex: 1 }}>
                        {ui.__("value")}
                    </div>
                </pr.HFlex>
                <pr.HFlex
                    style={{
                        width: "100%",
                        background: "#eee",
                        padding: 4
                    }}
                >
                    <div style={{ flex: 2 }}>
                        {ui.__("loan.minAmount")}
                    </div>
                    <div style={{ flex: 1 }}>
                        {this.props.interestRates.interestRates ?
                            this.props.interestRates.interestRates.minAmount.toLocaleString(config.LOCALE) :
                            "-"}
                    </div>
                </pr.HFlex>
                <pr.HFlex
                    style={{
                        width: "100%",
                        background: "white",
                        padding: 4
                    }}
                >
                    <div style={{ flex: 2 }}>
                        {ui.__("loan.maxAmount")}
                    </div>
                    <div style={{ flex: 1 }}>
                        {this.props.interestRates.interestRates ?
                            this.props.interestRates.interestRates.maxAmount.toLocaleString(config.LOCALE) :
                            "-"}
                    </div>
                </pr.HFlex>
                <pr.HFlex
                    style={{
                        width: "100%",
                        background: "#eee",
                        padding: 4
                    }}
                >
                    <div style={{ flex: 2 }}>
                        {ui.__("loan.minDuration")}
                    </div>
                    <div style={{ flex: 1 }}>
                        {this.props.interestRates.interestRates ?
                            this.props.interestRates.interestRates.minDuration :
                            "-"}
                    </div>
                </pr.HFlex>
                <pr.HFlex
                    style={{
                        width: "100%",
                        background: "white",
                        padding: 4
                    }}
                >
                    <div style={{ flex: 2 }}>
                        {ui.__("loan.maxDuration")}
                    </div>
                    <div style={{ flex: 1 }}>
                        {this.props.interestRates.interestRates ?
                            this.props.interestRates.interestRates.maxDuration :
                            "-"}
                    </div>
                </pr.HFlex>
            </div>
        );

        return <div style={{ width: "80%" }}>
            <pr.Title>{ui.__("currentInterestRates")}</pr.Title><br />
            {meta}
            <pr.TextH3 style={{ marginTop: theme.MARGIN_BIG, marginBottom: theme.MARGIN }}>{ui.__("header.interestRates")}</pr.TextH3>
            {current}
            <RepresentativeExample
                style={{ marginTop: theme.MARGIN_BIG }}
                interestSet={this.props.interestRates.interestRates}
            />
        </div>;
    }

    renderRepEx() {
        const interestRates = this.props.interestRates.interestRatesUpcoming || this.props.interestRates.interestRates;
        return (
            <div>
                <form.FormsyText
                    required={true}
                    name={"repExAmount"}
                    validations="isInt"
                    floatingLabelText={ui.__("representativeExample.amount")}
                    value={interestRates ? interestRates.repExes[0].amount : 20000}
                />
                <pr.HFlex style={{ marginTop: theme.MARGIN_BIG }}>
                    <RepEx interestRates={interestRates} index={0} style={{ maxWidth: "256px", marginRight: theme.MARGIN }} />
                    <RepEx interestRates={interestRates} index={1} />
                </pr.HFlex>
            </div>
        );
    }

    onFormChange = (currentValues: any, isChanged: boolean) => {
        if (isChanged) {
            debug.log(currentValues);

            // we are going to propagate duration range changes to the local observable durationArray.
            // the durationArray serves as a rerender trigger for the form them = all textfields per year are going to be rerendered
            const minDuration = _.parseInt(currentValues.minDuration);
            const maxDuration = _.parseInt(currentValues.maxDuration);

            if (_.isFinite(minDuration)
                && _.isFinite(maxDuration)
                && minDuration < maxDuration
                && _.isInteger(minDuration)
                && _.isInteger(maxDuration)
                && minDuration >= CONFIG_DURATION_MIN
                && minDuration <= CONFIG_DURATION_MAX
                && maxDuration >= CONFIG_DURATION_MIN
                && maxDuration <= CONFIG_DURATION_MAX) {

                this.durationArray = _.range(minDuration, maxDuration + 1);
                this.defaultDuration = getDefaultDuration(minDuration, maxDuration);
            }

            const minAmount = _.parseInt(currentValues.minAmount);
            const maxAmount = _.parseInt(currentValues.maxAmount);

            if (_.isFinite(minAmount)
                && _.isFinite(maxAmount)
                && minAmount < maxAmount
                && _.isInteger(minAmount)
                && _.isInteger(maxAmount)
                && minAmount >= CONFIG_AMOUNT_MIN
                && minAmount <= CONFIG_AMOUNT_MAX
                && maxAmount >= CONFIG_AMOUNT_MIN
                && maxAmount <= CONFIG_AMOUNT_MAX
                && minAmount % AMOUNT_RANGE_STEP === 0
                && maxAmount % AMOUNT_RANGE_STEP === 0) {

                this.defaultAmount = getDefaultAmount(minAmount, maxAmount);
            }
        }
    }

    render() {
        const interestRates = this.props.interestRates.interestRatesUpcoming || this.props.interestRates.interestRates;

        const originalDuration = this.props.interestRates.interestRatesUpcoming
            ? this.props.interestRates.durationArrayUpcoming
            : this.props.interestRates.durationArray;

        const upcoming = this.durationArray.map((value, index) => {

            // this.durationArray may be changed by the user, thus need to default to the values
            // from the originalDuration and map those actual value on the ui components!
            const originalIndex = originalDuration.indexOf(value);

            const plainInterest = (interestRates
                ? originalIndex < interestRates.interestInPercent.length
                    ? interestRates.interestInPercent[originalIndex]
                    : undefined
                : undefined);

            const effectiveInterest = (interestRates
                ? originalIndex < interestRates.interestEffectiveInPercent.length
                    ? interestRates.interestEffectiveInPercent[originalIndex]
                    : undefined
                : undefined);

            return (
                <pr.HFlex key={index}>
                    <form.FormsyText
                        required={true}
                        name={"interest" + value.toString()}
                        validations={{
                            isPositiveCurrency: true,
                            minValue: function (values: any, val: any) {
                                return _.isFinite(_.parseInt(val)) && parseLocalFloat(val) > 0;
                            },
                            maxValue: function (values: any, val: any) {
                                return _.isFinite(_.parseInt(val)) && parseLocalFloat(val) < 100;
                            },
                        }}
                        validationErrors={{
                            isPositiveCurrency: ui.__("error.interestInvalid"),
                            minValue: ui.__("validation.minValue", { value: 0 }),
                            maxValue: ui.__("validation.maxValue", { value: 100 }),
                        }}
                        floatingLabelText={value === 1 ? `Sollzins 1 Jahr` : `Sollzins ${value} Jahre`}
                        value={plainInterest && _.isFinite(plainInterest) ? plainInterest.toLocaleString(config.LOCALE) : undefined}
                    />
                    <form.FormsyText
                        required={true}
                        name={"interestEffective" + value.toString()}
                        validations={{
                            isPositiveCurrency: true,
                            minValue: function (values: any, val: any) {
                                return _.isFinite(_.parseInt(val)) && parseLocalFloat(val) > 0;
                            },
                            maxValue: function (values: any, val: any) {
                                return _.isFinite(_.parseInt(val)) && parseLocalFloat(val) < 100;
                            },
                        }}
                        validationErrors={{
                            isPositiveCurrency: ui.__("error.interestInvalid"),
                            minValue: ui.__("validation.minValue", { value: 0 }),
                            maxValue: ui.__("validation.maxValue", { value: 100 }),
                        }}
                        floatingLabelText={value === 1 ? `Effektivzins 1 Jahr` : `Effektivzins ${value} Jahre`}
                        value={effectiveInterest && _.isFinite(effectiveInterest) ? effectiveInterest.toLocaleString(config.LOCALE) : undefined}
                        style={{ marginLeft: theme.MARGIN }}
                    />
                </pr.HFlex>
            );
        });

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return (
            <div>
                {/* https://github.com/orgs/react-hook-form/discussions/2549 */}

                <Formsy.Form
                    onKeyDown={(e: any) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                        }
                    }}
                    onChange={this.onFormChange}
                    onValidSubmit={this.onSubmit}
                    onValid={() => this.formIsValid = true}
                    onInvalid={() => this.formIsValid = false}
                    ref={(c: any) => {
                        this.formRef = c;
                    }}
                >
                    <div style={{ textAlign: "right", margin: theme.MARGIN_BIG, marginBottom: 0 }}>
                        <mui.FlatButton
                            label={ui.__("password.change")}
                            style={{ width: 300 }}
                            backgroundColor={theme.LIGHT_BLUE}
                            onClick={() => this.props.history.push("/set-password")}
                        /><br />
                        {this.props.interestRates.interestRates && this.props.auth.isAdmin &&
                            <mui.FlatButton
                                label={ui.__("downloadHistory")}
                                style={{ width: 300, marginTop: theme.MARGIN }}
                                backgroundColor={theme.LIGHT_BLUE}
                                onClick={this.downloadHistory}
                            />
                        }
                    </div>

                    {this.props.interestRates.interestRates &&
                        <div>
                            <pr.FormContainer>
                                {this.renderCurrent()}
                            </pr.FormContainer>
                            <pr.Separator />
                        </div>
                    }


                    <pr.FormContainer>
                        {this.props.interestRates.interestRates && <pr.Title>{ui.__("upcomingInterestRates")}</pr.Title>}
                        <mui.DatePicker
                            floatingLabelText={ui.__("validFrom")}
                            shouldDisableDate={(date: Date) => date.getTime() < today.getTime()}
                            onChange={(event: any, date: Date) => this.validFrom = date}
                            locale="de" // for input field localization
                            DateTimeFormat={DateTimeFormat} // for calendar localization
                            value={this.validFrom}
                        />

                        <pr.HFlex>
                            <form.FormsyText
                                required={true}
                                name="minAmount"
                                validations={{
                                    isInt: true,
                                    minAmount: function (values: any, value: any) {
                                        const comparison = _.parseInt(values.maxAmount);
                                        return _.isFinite(comparison)
                                            ? _.parseInt(value) < comparison
                                            : true;
                                    },
                                    minEqualsValue: function (values: any, value: any) {
                                        return _.parseInt(value) >= CONFIG_AMOUNT_MIN;
                                    },
                                    maxEqualsValue: function (values: any, value: any) {
                                        return _.parseInt(value) <= CONFIG_AMOUNT_MAX;
                                    },
                                    modByValue: function (values: any, value: any) {
                                        return _.parseInt(value) % AMOUNT_RANGE_STEP === 0; // we still have a hardcoded step of AMOUNT_RANGE_STEP
                                    },
                                }}
                                validationErrors={{
                                    isInt: ui.__("validation.isInt"),
                                    minAmount: ui.__("validation.min"),
                                    minEqualsValue: ui.__("validation.minEqualsValue", { value: CONFIG_AMOUNT_MIN.toLocaleString(config.LOCALE) }),
                                    maxEqualsValue: ui.__("validation.maxEqualsValue", { value: CONFIG_AMOUNT_MAX.toLocaleString(config.LOCALE) }),
                                    modByValue: ui.__("validation.modByValue", { value: AMOUNT_RANGE_STEP.toLocaleString(config.LOCALE) }),
                                }}
                                floatingLabelText={ui.__("loan.minAmount")}
                                value={interestRates ? interestRates.minAmount :
                                    undefined}
                            />
                            <form.FormsyText
                                required={true}
                                name="maxAmount"
                                validations={{
                                    isInt: true,
                                    maxAmount: function (values: any, value: any) {
                                        const comparison = _.parseInt(values.minAmount);
                                        return _.isFinite(comparison)
                                            ? _.parseInt(value) > comparison
                                            : true;
                                    },
                                    minEqualsValue: function (values: any, value: any) {
                                        return _.parseInt(value) >= CONFIG_AMOUNT_MIN;
                                    },
                                    maxEqualsValue: function (values: any, value: any) {
                                        return _.parseInt(value) <= CONFIG_AMOUNT_MAX;
                                    },
                                    modByValue: function (values: any, value: any) {
                                        return _.parseInt(value) % AMOUNT_RANGE_STEP === 0; // we still have a hardcoded step of AMOUNT_RANGE_STEP
                                    },
                                }}
                                validationErrors={{
                                    isInt: ui.__("validation.isInt"),
                                    maxAmount: ui.__("validation.max"),
                                    minEqualsValue: ui.__("validation.minEqualsValue", { value: CONFIG_AMOUNT_MIN.toLocaleString(config.LOCALE) }),
                                    maxEqualsValue: ui.__("validation.maxEqualsValue", { value: CONFIG_AMOUNT_MAX.toLocaleString(config.LOCALE) }),
                                    modByValue: ui.__("validation.modByValue", { value: AMOUNT_RANGE_STEP.toLocaleString(config.LOCALE) }),
                                }}
                                floatingLabelText={ui.__("loan.maxAmount")}
                                value={interestRates ? interestRates.maxAmount :
                                    undefined}
                                style={{ marginLeft: theme.MARGIN }}
                            />
                            <mui.TextField
                                style={{ maxWidth: 192, marginLeft: 20 }}
                                disabled={true}
                                value={this.defaultAmount}
                                floatingLabelText="Standard-Betrag (mean)"
                            />
                            {/* <div style={{ maxWidth: 256, paddingLeft: theme.MARGIN, display: "flex", alignItems: "flex-end" }}>
                                <p style={{ fontSize: 14, fontStyle: "italic" }}>
                                    <small>Standard-Wert:</small>
                                </p>
                            </div> */}
                        </pr.HFlex>
                        <pr.HFlex>
                            <form.FormsyText
                                required={true}
                                name="minDuration"
                                validations={{
                                    isInt: true,
                                    minDuration: function (values: any, value: any) {
                                        const comparison = _.parseInt(values.maxDuration);
                                        return _.isFinite(comparison)
                                            ? _.parseInt(value) < comparison
                                            : true;
                                    },
                                    minEqualsValue: function (values: any, value: any) {
                                        return _.parseInt(value) >= CONFIG_DURATION_MIN;
                                    },
                                    maxEqualsValue: function (values: any, value: any) {
                                        return _.parseInt(value) <= CONFIG_DURATION_MAX;
                                    },
                                }}
                                validationErrors={{
                                    isInt: ui.__("validation.isInt"),
                                    minDuration: ui.__("validation.min"),
                                    minEqualsValue: ui.__("validation.minEqualsValue", { value: CONFIG_DURATION_MIN }),
                                    maxEqualsValue: ui.__("validation.maxEqualsValue", { value: CONFIG_DURATION_MAX }),
                                }}
                                floatingLabelText={ui.__("loan.minDuration")}
                                value={interestRates ? interestRates.minDuration :
                                    undefined}
                            />
                            <form.FormsyText
                                required={true}
                                name="maxDuration"
                                validations={{
                                    isInt: true,
                                    maxDuration: function (values: any, value: any) {
                                        const comparison = _.parseInt(values.minDuration);
                                        return _.isFinite(comparison)
                                            ? _.parseInt(value) > comparison
                                            : true;
                                    },
                                    minEqualsValue: function (values: any, value: any) {
                                        return _.parseInt(value) >= CONFIG_DURATION_MIN;
                                    },
                                    maxEqualsValue: function (values: any, value: any) {
                                        return _.parseInt(value) <= CONFIG_DURATION_MAX;
                                    },
                                }}
                                validationErrors={{
                                    isInt: ui.__("validation.isInt"),
                                    maxDuration: ui.__("validation.max"),
                                    minEqualsValue: ui.__("validation.minEqualsValue", { value: CONFIG_DURATION_MIN }),
                                    maxEqualsValue: ui.__("validation.maxEqualsValue", { value: CONFIG_DURATION_MAX }),
                                }}
                                floatingLabelText={ui.__("loan.maxDuration")}
                                value={interestRates ? interestRates.maxDuration :
                                    undefined}
                                style={{ marginLeft: theme.MARGIN }}
                            />
                            <mui.TextField
                                style={{ maxWidth: 192, marginLeft: 20 }}
                                disabled={true}
                                value={this.defaultDuration}
                                floatingLabelText="Standard-Laufzeit (mean)"
                            />
                        </pr.HFlex>

                        <pr.TextH3 style={{ marginTop: theme.MARGIN_BIG }}>{ui.__("header.interestRates")}</pr.TextH3>
                        {upcoming}

                        {/* </pr.FormContainer>
                    <pr.Separator />
                    <pr.FormContainer> */}

                        <pr.TextH3 style={{ marginTop: theme.MARGIN_BIG, maxWidth: "80%" }}>{ui.__("representativeExample.title")}</pr.TextH3>

                        {this.renderRepEx()}

                        <pr.HCenterDiv style={{ marginTop: theme.MARGIN_BIG }}>
                            <mui.RaisedButton
                                label={ui.__("saveAll")}
                                style={{ minWidth: theme.ACTION_BUTTON_WIDTH, width: "100%" }}
                                fullWidth={true}
                                primary={true}
                                type="submit"
                                disabled={this.formIsValid === false}
                            />
                        </pr.HCenterDiv>
                    </pr.FormContainer>

                </Formsy.Form>
            </div >
        );
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/InterestRatesForm.tsx